import {
  Box,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material';
import { useFormik } from 'formik';

import { useTranslation } from 'react-i18next';

import { mutation } from 'operations/mutation';
import { ModelTypes, RoutePaths } from 'types/types';
import { useSetRecoilState } from 'recoil';
import {
  toastStateSelector,
  ToastSeverity,
  dialogState,
  projectState,
} from 'state';
import { getProjectValidationSchema, initialProjectValues } from 'forms';
import { Project } from 'api/models';
import { getRoutePath } from 'utils';
import { useNavigate } from 'react-router-dom';
import { query } from 'operations';

export function AddProjectDialog() {
  const closeDialog = useSetRecoilState(dialogState);
  const { t, i18n } = useTranslation();
  const setToast = useSetRecoilState(toastStateSelector);
  const navigate = useNavigate();
  const setProjectStore = useSetRecoilState(projectState);

  const formik = useFormik({
    initialValues: initialProjectValues,
    validationSchema: getProjectValidationSchema(t),
    onSubmit: async (values, { resetForm }) => {
      const { name, shortName, description } = values;

      try {
        const { data: savedProject } = await mutation<Project>({
          type: ModelTypes.project,
          body: {
            name,
            shortName,
            description,
            configuration: {
              automaticProjectId: true,
              editableProjectId: true,
              samplesEnabled: false,
              queryModuleEnabled: true,
              repeatingFieldsEnabled: false,
            },

            id: '',
          },
        });

        const { data: projects } = await query<Project[]>({
          type: ModelTypes.project,
        });

        setProjectStore({
          projects,
          selectedProject: savedProject,
        });

        resetForm();
        closeDialog({ isOpen: false });
        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });

        navigate(
          getRoutePath(RoutePaths.projectDashboard, {
            projectId: savedProject?.id,
          }),
        );
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Box>
      <DialogTitle>{t('dialogs.addProject.title')}</DialogTitle>
      <DialogContent>
        <form>
          <TextField
            fullWidth
            margin="normal"
            id="project-creation-name"
            inputProps={{ 'data-testid': 'project-creation-name' }}
            label={t('projectCreation.name') + ' *'}
            type="text"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <TextField
            fullWidth
            margin="normal"
            id="project-creation-shortName"
            inputProps={{ 'data-testid': 'project-creation-shortName' }}
            label={t('projectCreation.shortName') + ' *'}
            type="text"
            name="shortName"
            value={formik.values.shortName}
            onChange={formik.handleChange}
            error={formik.touched.shortName && Boolean(formik.errors.shortName)}
            helperText={formik.touched.shortName && formik.errors.shortName}
          />
          <TextField
            fullWidth
            margin="normal"
            id="project-creation-description"
            inputProps={{ 'data-testid': 'project-creation-description' }}
            label={t('projectCreation.description')}
            type="text"
            multiline={true}
            rows={4}
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => closeDialog({ isOpen: false })}
          data-testid="add-project-dialog-close-button"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          data-testid="add-project-dialog-submit-button"
          onClick={formik.submitForm}
          variant="contained"
          sx={{ mr: 2, mb: 1 }}
        >
          {t('general.create')}
        </Button>
      </DialogActions>
    </Box>
  );
}
